/**
 * The lead-attribution entry snapshot (CWS-101, decision ``D11``).
 *
 * Both lead-capture forms send four extra CRM fields — ``landing_page`` and,
 * when the entry URL carried them, ``utm_source``, ``utm_medium`` and
 * ``utm_campaign`` — so a lead can be grouped by the channel that produced it.
 * This restores attribution **without a cookie, without device storage and
 * without a new third-party script**, which is what the product owner chose
 * over HubSpot's tracking script precisely to keep the site's consent posture
 * unchanged (``SEO.md`` §10.2 ``D11``).
 *
 * **Why the snapshot is taken at module load.** This is a single-page app: by
 * the time a visitor submits, ``window.location`` describes whatever page they
 * are on, not the one their session entered on. ``src/main.tsx`` imports this
 * module eagerly (structural since CWS-196, when the lead modals stopped
 * reaching it through an eager import chain), so evaluating it *is* the
 * initial page load — the only moment the entry URL is still observable. First touch wins for the life of
 * the SPA session; a full reload starts a new session and a new snapshot.
 *
 * **Why the campaign fields are omitted when empty, at the builder.** A
 * returning visitor's second, untagged submission must not overwrite the
 * first-touch values already on the contact — HubSpot writes what it is sent,
 * and this portal silently drops what its forms do not declare
 * (``SYSTEM_DESIGN.md`` §15.3). ``landing_page`` is always non-empty, so it is
 * always sent. This answers §15.3's open question on new-optional-field
 * spelling for *these* fields, on the functional ground above rather than by
 * convention.
 *
 * 🔴 **Portal first, code second — always.** None of the four fields reaches
 * the CRM until both HubSpot forms declare them; until then submissions arrive,
 * the response says success, and the values are silently discarded. The
 * contract test proves what we *send*; only the portal step and one live
 * submission prove what HubSpot *keeps*.
 */

/** One lead's entry provenance, keyed the way the CRM forms name the fields. */
export interface LeadAttribution {
  /** The ``utm_source`` of the entry URL, or ``""`` when it carried none. */
  utm_source: string;
  /** The ``utm_medium`` of the entry URL, or ``""`` when it carried none. */
  utm_medium: string;
  /** The ``utm_campaign`` of the entry URL, or ``""`` when it carried none. */
  utm_campaign: string;
  /** The path and query the session entered on; ``"/"`` when unparseable. */
  landing_page: string;
}

/**
 * Derive the attribution snapshot from the parts of the entry location
 *
 * Pure, so the L1 tests can pin the mapping without touching
 * ``window.location``; the module's own snapshot below is the only caller in
 * production code.
 *
 * Args:
 *     search: The entry URL's query string, with or without the leading ``?``.
 *     landingPath: The entry URL's path and query. An empty value — which a
 *         missing ``window.location.pathname`` would produce — falls back to
 *         ``"/"`` so the field is never sent empty.
 *
 * Returns:
 *     The four attribution fields. Each ``utm_*`` value is ``""`` when the
 *     entry query did not carry it; parameters other than the three we send
 *     are ignored. Values are truncated — 100 characters per campaign
 *     parameter, 512 for the landing page — so nothing an entry URL carries
 *     can grow past what a CRM field will accept.
 */
export function captureLeadAttribution(
  search: string,
  landingPath: string,
): LeadAttribution {
  const params = new URLSearchParams(search);
  return {
    // Truncated at capture, deliberately: the entry URL is attacker- and
    // ambient-controlled text, and the snapshot rides into every submission
    // the session makes. A value long enough to trip HubSpot's own validation
    // would fail the whole submission — the report visitor keeps their PDF
    // (D2) but the lead vanishes with nothing beyond a console line. The caps
    // are far above any real campaign value and far below anything a CRM
    // field would reject.
    utm_source: (params.get("utm_source") ?? "").slice(0, 100),
    utm_medium: (params.get("utm_medium") ?? "").slice(0, 100),
    utm_campaign: (params.get("utm_campaign") ?? "").slice(0, 100),
    landing_page: (landingPath || "/").slice(0, 512),
  };
}

// The entry snapshot. Evaluated once, at module load — see the module
// docstring for why that moment is the only one that observes the entry URL.
// Guarded the way `languageResolution.ts` guards its own module-scope reads:
// `manualChunks` pins this module into `shared`, which the CWS-57 prerender
// bundle evaluates in Node at import time, where there is no entry URL to
// observe — the empty snapshot is unreachable in a browser, which is the only
// place a lead is ever captured.
let entry: LeadAttribution =
  typeof window === "undefined"
    ? captureLeadAttribution("", "")
    : captureLeadAttribution(
        window.location.search,
        window.location.pathname + window.location.search,
      );

/**
 * Read the entry snapshot the payload builders attach
 *
 * Returns:
 *     The snapshot captured at module load, or whatever
 *     :func:`setLeadAttributionForTests` last installed.
 */
export function getLeadAttribution(): LeadAttribution {
  return entry;
}

/**
 * Install a snapshot in place of the module-load capture
 *
 * Test seam, and nothing else: production reads the real entry URL captured
 * above, and nothing in ``src/`` outside test files may call this. The
 * envelope contract (:mod:`src/lib/hubspotContract.test.ts`) needs it because
 * its jsdom harness URL carries no campaign parameters — the snapshot it wants
 * to assert on has to be installed rather than awaited.
 *
 * Args:
 *     snapshot: The attribution the next submissions should report.
 */
export function setLeadAttributionForTests(snapshot: LeadAttribution): void {
  entry = snapshot;
}

/**
 * Build the CRM field list for the entry snapshot
 *
 * ``landing_page`` is always included; each ``utm_*`` field only when
 * non-empty, so an untagged later submission preserves the first-touch values
 * already on the contact (module docstring). Both modals call this, which is
 * what keeps the two payload builders from growing their own copies of the
 * rule — the two-places-disagreeing failure ``SYSTEM_DESIGN.md`` §15.2 exists
 * to prevent.
 *
 * Args:
 *     snapshot: The attribution to encode. Defaults to the module snapshot,
 *         which is what both builders send.
 *
 * Returns:
 *     ``{name, value}`` pairs in a fixed order — landing page first, then the
 *     campaign parameters that are present — ready to spread into a payload's
 *     ``fields`` array.
 */
export function attributionFields(
  snapshot: LeadAttribution = getLeadAttribution(),
): Array<{ name: string; value: string }> {
  const fields = [{ name: "landing_page", value: snapshot.landing_page }];
  for (const key of ["utm_source", "utm_medium", "utm_campaign"] as const) {
    if (snapshot[key] !== "") {
      fields.push({ name: key, value: snapshot[key] });
    }
  }
  return fields;
}
